/**
 * Restore prompt for an account scheduled for deletion (#1748 Slice 1).
 *
 * App's identity gate renders this whenever `GET /me` reports
 * `deletionPending`. Recovery is DELIBERATE: a re-logged-in user lands
 * here and must click Restore — logging in never auto-restores. Restore
 * hits the explicit recovery endpoint, then refreshes identity so the app
 * loads normally (the next `/me` no longer reports `deletionPending`).
 */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, Card, CardContent } from '@/components/ui'
import { accountAPI } from '@/lib/api'
import { useIdentity } from '@/hooks/useIdentity'
import { authClient } from '@/auth/authClient'

function daysLeft(recoverableUntil?: number): number | null {
  if (!recoverableUntil) return null
  return Math.max(0, Math.ceil((recoverableUntil - Date.now()) / (24 * 60 * 60 * 1000)))
}

export function AccountDeletionPending({ recoverableUntil }: { recoverableUntil?: number }) {
  const { refreshIdentity } = useIdentity()
  const navigate = useNavigate()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const days = daysLeft(recoverableUntil)

  async function handleRestore() {
    setError(null)
    setBusy(true)
    try {
      await accountAPI.recover()
      // /me no longer reports deletionPending → the gate re-renders the app.
      await refreshIdentity()
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't restore your account. Try again.")
      setBusy(false)
    }
  }

  async function handleLogout() {
    await authClient.signOut()
    navigate('/auth/login', { replace: true })
  }

  return (
    <div className="min-h-dvh flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardContent className="p-6 space-y-4 text-center">
          <h1 className="text-lg font-semibold">Your account is scheduled for deletion</h1>
          <p className="text-sm text-muted-foreground">
            {days === null
              ? 'You can still restore it during the recovery window.'
              : `You have ${days} day${days === 1 ? '' : 's'} left to restore it.`}{' '}
            Restoring cancels the deletion and brings back the organizations you
            own. You've left the organizations you shared with others — rejoin
            those by invitation.
          </p>
          {error && (
            <p className="text-sm text-destructive" role="alert">
              {error}
            </p>
          )}
          <div className="space-y-2">
            <Button className="w-full" onClick={handleRestore} disabled={busy}>
              {busy ? 'Restoring…' : 'Restore my account'}
            </Button>
            <Button variant="ghost" className="w-full" onClick={handleLogout} disabled={busy}>
              Log out — keep it scheduled
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
